import React, { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { CheckCircle2 } from 'lucide-react';
import { api } from '../api/client';
import { useAuth } from '../contexts/AuthContext';
import { PLANS, Tier } from '../config/plans';
import { Banner, Button, Card, Spinner, buttonClasses } from '../components/ui';

const PaymentSuccess: React.FC = () => {
  const [searchParams] = useSearchParams();
  const { loadUser, user } = useAuth();
  const [isVerifying, setIsVerifying] = useState(true);
  const [error, setError] = useState('');
  const [verifiedTier, setVerifiedTier] = useState<string | null>(null);

  const verifyPayment = async () => {
    const sessionId = searchParams.get('session_id');

    if (!sessionId) {
      setError('Keine Session-ID gefunden');
      setIsVerifying(false);
      return;
    }

    setIsVerifying(true);
    setError('');

    try {
      // Verify payment with backend
      const response = await api.post('/payment/verify-session', { sessionId });

      // Use tier from verify-session response (authoritative, avoids DB replication lag)
      if (response.data?.user?.tier) {
        setVerifiedTier(response.data.user.tier);
      }

      // Also reload user data to update auth context
      await loadUser();

      setIsVerifying(false);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Fehler bei der Überprüfung der Zahlung');
      setIsVerifying(false);
    }
  };

  useEffect(() => {
    verifyPayment();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const activeTier = ((verifiedTier || user?.tier || 'free').toLowerCase() as Tier);
  const planName = PLANS[activeTier]?.name ?? PLANS.free.name;

  return (
    <div className="mx-auto flex w-full max-w-narrow flex-col items-center py-12">
      {isVerifying ? (
        <div className="flex flex-col items-center gap-4 text-center">
          <Spinner size={28} />
          <p className="text-muted">Zahlung wird überprüft...</p>
        </div>
      ) : error ? (
        <Banner
          tone="danger"
          title="Zahlung konnte nicht bestätigt werden"
          action={
            <Button variant="secondary" onClick={verifyPayment}>
              Erneut versuchen
            </Button>
          }
          className="w-full"
        >
          {error}
        </Banner>
      ) : (
        <Card padding="lg" className="flex w-full flex-col items-center gap-3 text-center">
          <CheckCircle2 className="h-12 w-12 text-success-text" aria-hidden="true" />
          <h1 className="font-display text-2xl font-semibold text-ink">Zahlung erfolgreich</h1>
          <p className="text-muted">Du bist jetzt bei {planName}. Danke für dein Vertrauen!</p>
          <Link to="/dashboard" className={buttonClasses('primary')}>
            Zu deinen Suchen
          </Link>
          {user?.email && (
            <p className="text-sm text-muted">Die Bestätigung haben wir an deine E-Mail-Adresse geschickt.</p>
          )}
        </Card>
      )}
    </div>
  );
};

export default PaymentSuccess;
